import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Braces, Binary, Link as LinkIcon, FileCode, Regex, Fingerprint, ShieldCheck,
  Palette, Blend, Youtube, Slash, Diff, Ruler, Clock, ImageDown, Copy, Download,
  Hash, Tag, AlignLeft, Contrast, KeyRound, FileJson, Smile, Calendar, Percent, Globe2,
} from "lucide-react";

const input = "w-full border border-slate-200 bg-white rounded-lg px-3 py-2.5 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors font-mono text-sm";
const inputNorm = "w-full border border-slate-200 bg-white rounded-lg px-3 py-2.5 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors";
const label = "text-xs font-semibold uppercase tracking-widest text-slate-500 block mb-2";
const btn = "inline-flex items-center gap-2 bg-indigo-600 text-white rounded-lg px-5 py-2.5 font-medium hover:bg-indigo-700 active:scale-95 transition disabled:opacity-50";
const ghost = "inline-flex items-center gap-2 border border-slate-200 rounded-lg px-4 py-2.5 font-medium hover:bg-slate-50 active:scale-95 transition text-sm";
const pre = "bg-slate-900 text-slate-100 text-xs rounded-xl p-4 overflow-auto max-h-80 font-mono whitespace-pre-wrap break-all";

const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

/* 1. JSON Formatter/Validator */
function JsonTool() {
  const [text, setText] = useState('{"name":"Julius","stack":["react","fastapi"],"active":true}');
  const [out, setOut] = useState("");
  const [err, setErr] = useState("");
  const run = (indent = 2) => {
    try {
      const p = JSON.parse(text);
      setOut(JSON.stringify(p, null, indent));
      setErr("");
    } catch (e) { setErr(e.message); setOut(""); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <label className={label}>Input JSON</label>
        <textarea data-testid="json-input" rows={12} className={input} value={text} onChange={(e) => setText(e.target.value)} />
        <div className="flex gap-2 mt-3 flex-wrap">
          <button className={btn} onClick={() => run(2)} data-testid="json-format">Format (2 spaces)</button>
          <button className={ghost} onClick={() => run(4)}>4 spaces</button>
          <button className={ghost} onClick={() => run(0)}>Minify</button>
        </div>
      </div>
      <div>
        <label className={label}>Output {err && <span className="text-red-500 normal-case">— {err}</span>}</label>
        <pre className={pre} data-testid="json-output">{out || "// Formatted JSON appears here"}</pre>
        {out && <button className={ghost + " mt-3"} onClick={() => copy(out)}><Copy className="w-3.5 h-3.5" /> Copy</button>}
      </div>
    </div>
  );
}

/* 2. Base64 Encode/Decode */
function Base64Tool() {
  const [text, setText] = useState("Hello, Julius!");
  const [mode, setMode] = useState("encode");
  const out = useMemo(() => {
    try {
      if (mode === "encode") return btoa(unescape(encodeURIComponent(text)));
      return decodeURIComponent(escape(atob(text)));
    } catch { return "// Invalid input for " + mode; }
  }, [text, mode]);
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button data-testid="b64-encode" className={mode === "encode" ? btn : ghost} onClick={() => setMode("encode")}>Encode</button>
        <button data-testid="b64-decode" className={mode === "decode" ? btn : ghost} onClick={() => setMode("decode")}>Decode</button>
      </div>
      <div>
        <label className={label}>{mode === "encode" ? "Plain text" : "Base64 text"}</label>
        <textarea data-testid="b64-input" rows={4} className={input} value={text} onChange={(e) => setText(e.target.value)} />
      </div>
      <div>
        <label className={label}>Result</label>
        <pre className={pre} data-testid="b64-output">{out}</pre>
        <button className={ghost + " mt-3"} onClick={() => copy(out)}><Copy className="w-3.5 h-3.5" /> Copy</button>
      </div>
    </div>
  );
}

/* 3. URL Encode/Decode */
function UrlEncoderTool() {
  const [text, setText] = useState("https://juliusmason.com/search?q=digital marketing & AI");
  const [mode, setMode] = useState("encode");
  const out = useMemo(() => {
    try {
      return mode === "encode" ? encodeURIComponent(text) : decodeURIComponent(text);
    } catch { return "// Invalid"; }
  }, [text, mode]);
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button className={mode === "encode" ? btn : ghost} onClick={() => setMode("encode")}>Encode</button>
        <button className={mode === "decode" ? btn : ghost} onClick={() => setMode("decode")}>Decode</button>
      </div>
      <textarea data-testid="urlenc-input" rows={3} className={input} value={text} onChange={(e) => setText(e.target.value)} />
      <label className={label}>Result</label>
      <pre className={pre} data-testid="urlenc-output">{out}</pre>
      <button className={ghost} onClick={() => copy(out)}><Copy className="w-3.5 h-3.5" /> Copy</button>
    </div>
  );
}

/* 4. Markdown Preview */
function mdToHtml(md) {
  if (!md) return "";
  let h = md
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/^###### (.*)$/gm, "<h6>$1</h6>")
    .replace(/^##### (.*)$/gm, "<h5>$1</h5>")
    .replace(/^#### (.*)$/gm, "<h4>$1</h4>")
    .replace(/^### (.*)$/gm, "<h3>$1</h3>")
    .replace(/^## (.*)$/gm, "<h2>$1</h2>")
    .replace(/^# (.*)$/gm, "<h1>$1</h1>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>')
    .replace(/^\s*[-*] (.*)$/gm, "<li>$1</li>")
    .replace(/(<li>.*<\/li>)/gs, "<ul>$1</ul>")
    .replace(/\n\n/g, "</p><p>")
    .replace(/^(?!<)/gm, "");
  return "<p>" + h + "</p>";
}
function MarkdownTool() {
  const [text, setText] = useState("# Hello Julius\n\nType **markdown** here and see a live *preview*.\n\n- Bullet one\n- Bullet two\n\n[Visit site](https://juliusmason.com)");
  const html = useMemo(() => mdToHtml(text), [text]);
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <label className={label}>Markdown</label>
        <textarea data-testid="md-input" rows={14} className={input} value={text} onChange={(e) => setText(e.target.value)} />
      </div>
      <div>
        <label className={label}>Preview</label>
        <div data-testid="md-preview" className="prose prose-slate max-w-none border border-slate-100 rounded-xl p-5 bg-slate-50/30 min-h-[300px]" dangerouslySetInnerHTML={{ __html: html }} />
        <button className={ghost + " mt-3"} onClick={() => copy(html)}><Copy className="w-3.5 h-3.5" /> Copy HTML</button>
      </div>
    </div>
  );
}

/* 5. Regex Tester */
function RegexTool() {
  const [pattern, setPattern] = useState("\\b\\w{5,}\\b");
  const [flags, setFlags] = useState("gi");
  const [text, setText] = useState("The quick brown fox jumps over the lazy dog.");
  const result = useMemo(() => {
    try {
      const re = new RegExp(pattern, flags);
      const matches = [...text.matchAll(re)];
      return { ok: true, matches: matches.map((m) => ({ match: m[0], index: m.index })) };
    } catch (e) { return { ok: false, error: e.message }; }
  }, [pattern, flags, text]);
  const highlighted = useMemo(() => {
    if (!result.ok || !pattern) return text;
    try {
      return text.replace(new RegExp(pattern, flags.includes("g") ? flags : flags + "g"), (m) => `<mark class="bg-indigo-100 text-indigo-700 rounded px-0.5">${m}</mark>`);
    } catch { return text; }
  }, [text, pattern, flags, result.ok]);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-3">
        <div className="col-span-3"><label className={label}>Pattern</label><input data-testid="regex-pattern" className={input} value={pattern} onChange={(e) => setPattern(e.target.value)} /></div>
        <div><label className={label}>Flags</label><input className={input} value={flags} onChange={(e) => setFlags(e.target.value)} placeholder="gim" /></div>
      </div>
      <div><label className={label}>Test string</label><textarea data-testid="regex-text" rows={4} className={inputNorm} value={text} onChange={(e) => setText(e.target.value)} /></div>
      {!result.ok ? (
        <p className="text-red-500 text-sm">Regex error: {result.error}</p>
      ) : (
        <>
          <div className="border border-slate-100 rounded-xl p-4 bg-slate-50/40" dangerouslySetInnerHTML={{ __html: highlighted }} />
          <p data-testid="regex-count" className="text-sm text-slate-500">{result.matches.length} match(es)</p>
        </>
      )}
    </div>
  );
}

/* 6. UUID Generator */
function UuidTool() {
  const [count, setCount] = useState(5);
  const [list, setList] = useState([]);
  const gen = () => {
    const arr = [];
    for (let i = 0; i < count; i++) arr.push(crypto.randomUUID ? crypto.randomUUID() : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => { const r = (Math.random() * 16) | 0; return (c === "x" ? r : (r & 0x3) | 0x8).toString(16); }));
    setList(arr);
  };
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <div><label className={label}>How many</label><input type="number" min={1} max={100} className={inputNorm + " w-32"} value={count} onChange={(e) => setCount(Number(e.target.value))} /></div>
        <button data-testid="uuid-generate" className={btn} onClick={gen}><Fingerprint className="w-4 h-4" /> Generate</button>
      </div>
      {list.length > 0 && (
        <div className="border border-slate-100 rounded-xl bg-slate-50/40 divide-y divide-slate-100" data-testid="uuid-list">
          {list.map((u, i) => (
            <div key={i} className="flex items-center justify-between p-3">
              <code className="text-sm font-mono">{u}</code>
              <button onClick={() => copy(u)} className="text-slate-400 hover:text-indigo-600"><Copy className="w-4 h-4" /></button>
            </div>
          ))}
          <div className="p-3 flex justify-end"><button onClick={() => copy(list.join("\n"))} className={ghost}><Copy className="w-3.5 h-3.5" /> Copy all</button></div>
        </div>
      )}
    </div>
  );
}

/* 7. Password Strength Checker */
function StrengthTool() {
  const [pw, setPw] = useState("");
  const score = useMemo(() => {
    if (!pw) return { s: 0, label: "—", tips: [] };
    let s = 0; const tips = [];
    if (pw.length >= 8) s++; else tips.push("Use at least 8 characters");
    if (pw.length >= 14) s++; else if (pw.length >= 8) tips.push("Aim for 14+ characters");
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++; else tips.push("Mix upper & lowercase");
    if (/\d/.test(pw)) s++; else tips.push("Add numbers");
    if (/[^A-Za-z0-9]/.test(pw)) s++; else tips.push("Add symbols");
    const labels = ["Very weak", "Weak", "Okay", "Good", "Strong", "Excellent"];
    return { s, label: labels[s], tips };
  }, [pw]);
  const colors = ["bg-red-500", "bg-orange-500", "bg-amber-500", "bg-lime-500", "bg-green-500", "bg-emerald-600"];
  return (
    <div className="space-y-5">
      <div><label className={label}>Enter a password to test</label><input data-testid="strength-input" type="text" className={inputNorm + " font-mono"} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Type or paste…" /></div>
      <div>
        <div className="flex items-center justify-between mb-2"><span className="font-mono text-xs uppercase tracking-widest text-slate-500">Strength</span><span data-testid="strength-label" className="font-medium">{score.label}</span></div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full transition-all ${colors[score.s]}`} style={{ width: `${(score.s / 5) * 100}%` }} /></div>
      </div>
      {score.tips.length > 0 && (
        <ul className="text-sm text-slate-500 list-disc ml-5 space-y-1">{score.tips.map((t, i) => <li key={i}>{t}</li>)}</ul>
      )}
    </div>
  );
}

/* 8. Color Picker & Palette */
function hexToRgb(h) { const m = h.replace("#", "").match(/.{2}/g) || []; return m.map((x) => parseInt(x, 16)); }
function rgbToHex(r, g, b) { return "#" + [r, g, b].map((n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0")).join(""); }
function ColorSwatch({ hex, l, big }) {
  return (
    <button onClick={() => copy(hex)} className={`flex-1 ${big ? "h-16" : "h-16"} rounded-lg border border-slate-100 relative overflow-hidden group`} style={{ backgroundColor: hex }}>
      <span className="absolute inset-x-0 bottom-0 text-[10px] font-mono bg-black/40 text-white py-0.5">{l || hex}</span>
    </button>
  );
}
function ColorTool() {
  const [c, setC] = useState("#4f46e5");
  const [r, g, b] = hexToRgb(c);
  const palette = useMemo(() => {
    const shades = [0.2, 0.4, 0.6, 0.8].map((k) => rgbToHex(r + (255 - r) * k, g + (255 - g) * k, b + (255 - b) * k));
    const darks = [0.2, 0.4, 0.6, 0.8].map((k) => rgbToHex(r * (1 - k), g * (1 - k), b * (1 - k)));
    const comp = rgbToHex(255 - r, 255 - g, 255 - b);
    return { shades, darks, comp };
  }, [r, g, b]);
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4">
        <input data-testid="color-input" type="color" value={c} onChange={(e) => setC(e.target.value)} className="w-24 h-24 rounded-lg border border-slate-200 cursor-pointer" />
        <div className="flex-1">
          <label className={label}>Hex</label>
          <input className={input} value={c} onChange={(e) => setC(e.target.value)} />
          <p className="text-sm text-slate-500 mt-2">rgb({r}, {g}, {b})</p>
        </div>
      </div>
      <div><p className={label}>Tints</p><div className="flex gap-2">{palette.shades.map((h, i) => <ColorSwatch key={i} hex={h} />)}</div></div>
      <div><p className={label}>Shades</p><div className="flex gap-2">{palette.darks.map((h, i) => <ColorSwatch key={i} hex={h} />)}</div></div>
      <div><p className={label}>Complementary</p><div className="flex gap-2"><ColorSwatch hex={c} l={"base " + c} /><ColorSwatch hex={palette.comp} l={"comp " + palette.comp} /></div></div>
    </div>
  );
}

/* 9. CSS Gradient Generator */
function GradientTool() {
  const [from, setFrom] = useState("#6366f1");
  const [to, setTo] = useState("#ec4899");
  const [angle, setAngle] = useState(135);
  const css = `background: linear-gradient(${angle}deg, ${from} 0%, ${to} 100%);`;
  return (
    <div className="space-y-5">
      <div data-testid="gradient-preview" className="h-48 rounded-xl" style={{ background: `linear-gradient(${angle}deg, ${from} 0%, ${to} 100%)` }} />
      <div className="grid grid-cols-3 gap-4">
        <div><label className={label}>From</label><input type="color" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full h-11 rounded border border-slate-200" /></div>
        <div><label className={label}>To</label><input type="color" value={to} onChange={(e) => setTo(e.target.value)} className="w-full h-11 rounded border border-slate-200" /></div>
        <div><label className={label}>Angle ({angle}°)</label><input type="range" min="0" max="360" value={angle} onChange={(e) => setAngle(Number(e.target.value))} className="w-full accent-indigo-600 mt-3" /></div>
      </div>
      <div className="relative"><pre className={pre} data-testid="gradient-css">{css}</pre><button onClick={() => copy(css)} className="absolute top-3 right-3 text-slate-300 hover:text-white"><Copy className="w-4 h-4" /></button></div>
    </div>
  );
}

/* 10. YouTube Thumbnail Grabber */
function YoutubeTool() {
  const [url, setUrl] = useState("https://www.youtube.com/watch?v=dQw4w9WgXcQ");
  const id = useMemo(() => {
    const m = url.match(/(?:v=|\/embed\/|youtu\.be\/|\/shorts\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : "";
  }, [url]);
  const qualities = [
    ["maxresdefault", "Max Res (1280×720)"], ["sddefault", "SD (640×480)"],
    ["hqdefault", "HQ (480×360)"], ["mqdefault", "MQ (320×180)"], ["default", "Default (120×90)"],
  ];
  return (
    <div className="space-y-5">
      <div><label className={label}>YouTube URL</label><input data-testid="yt-url" className={inputNorm} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Paste any YouTube link" /></div>
      {id ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="yt-thumbs">
          {qualities.map(([q, label]) => (
            <a key={q} href={`https://img.youtube.com/vi/${id}/${q}.jpg`} target="_blank" rel="noreferrer" download className="group border border-slate-100 rounded-xl overflow-hidden bg-white hover-lift">
              <img src={`https://img.youtube.com/vi/${id}/${q}.jpg`} alt={q} className="w-full aspect-video object-cover bg-slate-100" onError={(e) => { e.target.style.opacity = 0.3; }} />
              <div className="p-3 flex items-center justify-between"><span className="text-xs font-medium">{label}</span><Download className="w-4 h-4 text-indigo-600" /></div>
            </a>
          ))}
        </div>
      ) : <p className="text-slate-400 text-sm">Paste a valid YouTube URL to see thumbnails.</p>}
    </div>
  );
}

/* 11. Slug Generator */
function SlugTool() {
  const [text, setText] = useState("");
  const slug = useMemo(() => text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").replace(/-+/g, "-"), [text]);
  return (
    <div className="space-y-4">
      <div><label className={label}>Title / Text</label><input data-testid="slug-input" className={inputNorm} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. How To Grow Your Business In 2026" /></div>
      <div className="border border-slate-100 rounded-xl p-5 bg-slate-50/40 flex items-center justify-between">
        <code data-testid="slug-output" className="font-mono text-indigo-700 text-lg break-all">{slug || "your-slug-appears-here"}</code>
        {slug && <button onClick={() => copy(slug)} className="text-indigo-600 flex-shrink-0 ml-3"><Copy className="w-5 h-5" /></button>}
      </div>
      <p className="text-xs text-slate-400">Lowercased, accents stripped, spaces → dashes. Great for SEO-friendly URLs.</p>
    </div>
  );
}

/* 12. Text Diff */
function DiffTool() {
  const [a, setA] = useState("The quick brown fox jumps over the lazy dog.");
  const [b, setB] = useState("The quick red fox leaps over a sleeping dog.");
  const render = (left, right) => {
    const la = left.split(/(\s+)/); const lb = right.split(/(\s+)/);
    const n = Math.max(la.length, lb.length);
    const out = [];
    for (let i = 0; i < n; i++) {
      const x = la[i] || ""; const y = lb[i] || "";
      if (x === y) out.push({ w: x, t: "same" });
      else { if (x) out.push({ w: x, t: "del" }); if (y) out.push({ w: y, t: "add" }); }
    }
    return out;
  };
  const diff = useMemo(() => render(a, b), [a, b]);
  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <div><label className={label}>Original</label><textarea data-testid="diff-a" rows={6} className={inputNorm} value={a} onChange={(e) => setA(e.target.value)} /></div>
        <div><label className={label}>Changed</label><textarea data-testid="diff-b" rows={6} className={inputNorm} value={b} onChange={(e) => setB(e.target.value)} /></div>
      </div>
      <div data-testid="diff-output" className="border border-slate-100 rounded-xl p-5 bg-slate-50/40 leading-relaxed">
        {diff.map((d, i) => (
          <span key={i} className={d.t === "del" ? "bg-red-100 text-red-700 line-through" : d.t === "add" ? "bg-green-100 text-green-700" : ""}>{d.w}</span>
        ))}
      </div>
    </div>
  );
}

/* 13. Unit Converter */
const UNITS = {
  length: { m: 1, cm: 100, mm: 1000, km: 0.001, in: 39.3701, ft: 3.28084, yd: 1.09361, mi: 0.000621371 },
  weight: { kg: 1, g: 1000, mg: 1000000, lb: 2.20462, oz: 35.274 },
};
function UnitTool() {
  const [kind, setKind] = useState("length");
  const [val, setVal] = useState(1);
  const [from, setFrom] = useState("m");
  const [tempIn, setTempIn] = useState(25);
  const [tempFrom, setTempFrom] = useState("C");
  const results = useMemo(() => {
    if (kind === "temp") {
      const c = tempFrom === "C" ? Number(tempIn) : tempFrom === "F" ? (Number(tempIn) - 32) * 5 / 9 : Number(tempIn) - 273.15;
      return [["Celsius", c.toFixed(2) + " °C"], ["Fahrenheit", (c * 9 / 5 + 32).toFixed(2) + " °F"], ["Kelvin", (c + 273.15).toFixed(2) + " K"]];
    }
    const u = UNITS[kind]; const base = Number(val) / u[from];
    return Object.entries(u).map(([k, f]) => [k, (base * f).toPrecision(6)]);
  }, [kind, val, from, tempIn, tempFrom]);
  return (
    <div className="space-y-5">
      <div className="flex gap-2 flex-wrap">
        {["length", "weight", "temp"].map((k) => <button key={k} onClick={() => setKind(k)} className={kind === k ? btn : ghost}>{k[0].toUpperCase() + k.slice(1)}</button>)}
      </div>
      {kind === "temp" ? (
        <div className="grid grid-cols-2 gap-4">
          <div><label className={label}>Value</label><input type="number" className={inputNorm} value={tempIn} onChange={(e) => setTempIn(e.target.value)} /></div>
          <div><label className={label}>From</label><select className={inputNorm} value={tempFrom} onChange={(e) => setTempFrom(e.target.value)}><option>C</option><option>F</option><option>K</option></select></div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <div><label className={label}>Value</label><input type="number" className={inputNorm} value={val} onChange={(e) => setVal(e.target.value)} /></div>
          <div><label className={label}>From</label><select className={inputNorm} value={from} onChange={(e) => setFrom(e.target.value)}>{Object.keys(UNITS[kind]).map((u) => <option key={u}>{u}</option>)}</select></div>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="unit-results">
        {results.map(([k, v]) => (<div key={k} className="border border-slate-100 rounded-xl p-4 text-center"><div className="font-display text-xl text-indigo-600">{v}</div><div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{k}</div></div>))}
      </div>
    </div>
  );
}

/* 14. Timestamp Converter */
function TimestampTool() {
  const [ts, setTs] = useState(Math.floor(Date.now() / 1000));
  const [date, setDate] = useState(new Date().toISOString().slice(0, 19));
  const fromTs = useMemo(() => {
    const n = Number(ts); if (!n) return "";
    return new Date(n > 1e12 ? n : n * 1000).toISOString();
  }, [ts]);
  const toTs = useMemo(() => {
    const d = new Date(date); return isNaN(d) ? "" : Math.floor(d.getTime() / 1000);
  }, [date]);
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-3">
        <label className={label}>Unix timestamp → Date</label>
        <input data-testid="ts-unix" className={inputNorm + " font-mono"} value={ts} onChange={(e) => setTs(e.target.value)} />
        <div className="border border-slate-100 rounded-xl p-4 bg-slate-50/40"><code className="text-indigo-700 break-all" data-testid="ts-iso">{fromTs || "—"}</code></div>
        <button className={ghost} onClick={() => setTs(Math.floor(Date.now() / 1000))}><Clock className="w-4 h-4" /> Use now</button>
      </div>
      <div className="space-y-3">
        <label className={label}>Date → Unix timestamp</label>
        <input type="datetime-local" className={inputNorm} value={date} onChange={(e) => setDate(e.target.value)} />
        <div className="border border-slate-100 rounded-xl p-4 bg-slate-50/40"><code className="font-mono text-indigo-700" data-testid="ts-fromdate">{toTs || "—"}</code></div>
      </div>
    </div>
  );
}

/* 15. Image to Base64 */
function ImageBase64Tool() {
  const [b64, setB64] = useState("");
  const [name, setName] = useState("");
  const onUpload = (f) => {
    if (!f) return;
    setName(f.name);
    const r = new FileReader();
    r.onload = () => setB64(String(r.result));
    r.readAsDataURL(f);
  };
  return (
    <div className="space-y-5">
      <label className="block border-2 border-dashed border-slate-200 bg-slate-50/50 hover:bg-slate-50 rounded-xl p-8 text-center cursor-pointer transition-colors">
        <ImageDown className="w-8 h-8 text-slate-400 mx-auto mb-2" />
        {name || "Click to upload an image"}
        <input data-testid="imgb64-upload" type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files[0])} />
      </label>
      {b64 && (
        <>
          <div className="flex items-start gap-4 border border-slate-100 rounded-xl p-4"><img src={b64} alt="preview" className="w-24 h-24 object-cover rounded" /><p className="text-xs text-slate-500">Length: {b64.length.toLocaleString()} chars</p></div>
          <div className="relative"><pre className={pre} data-testid="imgb64-output">{b64}</pre><button onClick={() => copy(b64)} className="absolute top-3 right-3 text-slate-300 hover:text-white"><Copy className="w-4 h-4" /></button></div>
        </>
      )}
    </div>
  );
}

/* 16. UTM Link Builder */
function UtmBuilderTool() {
  const [url, setUrl] = useState("https://example.com/landing");
  const [f, setF] = useState({ source: "twitter", medium: "social", campaign: "launch", term: "", content: "" });
  const out = useMemo(() => {
    try {
      const u = new URL(url);
      const map = { utm_source: f.source, utm_medium: f.medium, utm_campaign: f.campaign, utm_term: f.term, utm_content: f.content };
      Object.entries(map).forEach(([k, v]) => { if (v) u.searchParams.set(k, v); });
      return u.toString();
    } catch { return "// invalid URL"; }
  }, [url, f]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="space-y-4">
      <div><label className={label}>Destination URL</label><input data-testid="utm-url" className={inputNorm} value={url} onChange={(e) => setUrl(e.target.value)} /></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input data-testid="utm-source" className={inputNorm} placeholder="Source (twitter, newsletter…)" value={f.source} onChange={set("source")} />
        <input data-testid="utm-medium" className={inputNorm} placeholder="Medium (social, email, cpc…)" value={f.medium} onChange={set("medium")} />
        <input data-testid="utm-campaign" className={inputNorm} placeholder="Campaign name" value={f.campaign} onChange={set("campaign")} />
        <input className={inputNorm} placeholder="Term (optional)" value={f.term} onChange={set("term")} />
        <input className={inputNorm + " md:col-span-2"} placeholder="Content variant (optional)" value={f.content} onChange={set("content")} />
      </div>
      <pre className={pre} data-testid="utm-output">{out}</pre>
      <button className={ghost} onClick={() => copy(out)}><Copy className="w-3.5 h-3.5" /> Copy tagged URL</button>
    </div>
  );
}

/* 17. Word & Character Counter */
function WordCounterTool() {
  const [text, setText] = useState("");
  const stats = useMemo(() => {
    const chars = text.length;
    const charsNoSpaces = text.replace(/\s/g, "").length;
    const words = (text.trim().match(/\S+/g) || []).length;
    const sentences = (text.match(/[^.!?]+[.!?]+/g) || []).length;
    const readingMin = Math.max(1, Math.round(words / 200));
    return { chars, charsNoSpaces, words, sentences, readingMin };
  }, [text]);
  return (
    <div className="space-y-4">
      <textarea data-testid="wc-input" rows={8} className={inputNorm} placeholder="Paste or type text here…" value={text} onChange={(e) => setText(e.target.value)} />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ["Words", stats.words, "wc-words"],
          ["Characters", stats.chars, "wc-chars"],
          ["No spaces", stats.charsNoSpaces, "wc-chars-ns"],
          ["Sentences", stats.sentences, "wc-sentences"],
          ["Read time", `${stats.readingMin} min`, "wc-readtime"],
        ].map(([k, v, tid]) => (
          <div key={k} className="border border-slate-100 rounded-lg p-4 bg-slate-50/60">
            <p className="text-[10px] uppercase font-mono tracking-widest text-slate-500 mb-1">{k}</p>
            <p className="font-display text-2xl" data-testid={tid}>{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* 18. Color Contrast Checker */
function ContrastTool() {
  const [fg, setFg] = useState("#111111");
  const [bg, setBg] = useState("#ffffff");
  const hexToRgb = (h) => { const s = h.replace("#", ""); return [0, 2, 4].map((i) => parseInt(s.slice(i, i + 2), 16)); };
  const relLum = (rgb) => {
    const [r, g, b] = rgb.map((v) => {
      v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  const ratio = useMemo(() => {
    try {
      const l1 = relLum(hexToRgb(fg)); const l2 = relLum(hexToRgb(bg));
      const [a, b] = l1 > l2 ? [l1, l2] : [l2, l1];
      return (a + 0.05) / (b + 0.05);
    } catch { return 0; }
  }, [fg, bg]);
  const grade = (r) => r >= 7 ? "AAA" : r >= 4.5 ? "AA" : r >= 3 ? "AA Large" : "Fail";
  const g = grade(ratio);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <label className="block"><span className={label}>Foreground</span><input data-testid="contrast-fg" type="color" value={fg} onChange={(e) => setFg(e.target.value)} className="h-12 w-full rounded-lg border border-slate-200" /></label>
        <label className="block"><span className={label}>Background</span><input data-testid="contrast-bg" type="color" value={bg} onChange={(e) => setBg(e.target.value)} className="h-12 w-full rounded-lg border border-slate-200" /></label>
      </div>
      <div className="p-8 rounded-xl border border-slate-100" style={{ background: bg, color: fg }}>
        <p className="font-display text-3xl mb-2">The quick brown fox</p>
        <p className="text-base">jumps over the lazy dog — this is body copy at 16px.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="border border-slate-100 rounded-lg p-4 bg-slate-50/60"><p className="text-[10px] uppercase font-mono tracking-widest text-slate-500 mb-1">Ratio</p><p className="font-display text-3xl" data-testid="contrast-ratio">{ratio.toFixed(2)}:1</p></div>
        <div className={`rounded-lg p-4 ${g === "Fail" ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}><p className="text-[10px] uppercase font-mono tracking-widest mb-1 opacity-80">WCAG</p><p className="font-display text-3xl" data-testid="contrast-grade">{g}</p></div>
      </div>
    </div>
  );
}

/* 19. Password Generator */
function PasswordGenTool() {
  const [len, setLen] = useState(16);
  const [opts, setOpts] = useState({ upper: true, lower: true, nums: true, symbols: true });
  const [out, setOut] = useState("");
  const gen = () => {
    let pool = "";
    if (opts.upper) pool += "ABCDEFGHJKLMNPQRSTUVWXYZ";
    if (opts.lower) pool += "abcdefghijkmnopqrstuvwxyz";
    if (opts.nums) pool += "23456789";
    if (opts.symbols) pool += "!@#$%^&*_-+=";
    if (!pool) { setOut(""); return; }
    const arr = new Uint32Array(len);
    crypto.getRandomValues(arr);
    setOut(Array.from(arr, (v) => pool[v % pool.length]).join(""));
  };
  React.useEffect(() => { gen(); /* eslint-disable-next-line */ }, []);
  const tog = (k) => setOpts({ ...opts, [k]: !opts[k] });
  return (
    <div className="space-y-4">
      <label className="block"><span className={label}>Length: {len}</span><input data-testid="pwgen-len" type="range" min="8" max="64" value={len} onChange={(e) => setLen(Number(e.target.value))} className="w-full" /></label>
      <div className="flex flex-wrap gap-2 text-sm">
        {[["upper", "A-Z"], ["lower", "a-z"], ["nums", "0-9"], ["symbols", "!@#"]].map(([k, l]) => (
          <label key={k} className={`px-3 py-2 rounded-lg border cursor-pointer transition ${opts[k] ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200"}`}>
            <input type="checkbox" checked={opts[k]} onChange={() => tog(k)} className="mr-2" data-testid={`pwgen-${k}`} />{l}
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-2"><button onClick={gen} className={btn} data-testid="pwgen-generate">Generate</button><button onClick={() => copy(out)} className={ghost}><Copy className="w-3.5 h-3.5" /> Copy</button></div>
      <pre className={pre} data-testid="pwgen-output">{out || "Click Generate"}</pre>
    </div>
  );
}

/* 20. JWT Decoder */
function JwtDecoderTool() {
  const [tok, setTok] = useState("eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJqdWxpdXMiLCJuYW1lIjoiSnVsaXVzIE1hc29uIiwiaWF0IjoxNzMwMDAwMDAwfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c");
  const parts = tok.split(".");
  const decode = (s) => { try { return JSON.stringify(JSON.parse(atob(s.replace(/-/g, "+").replace(/_/g, "/"))), null, 2); } catch { return "// invalid segment"; } };
  const header = parts[0] ? decode(parts[0]) : "";
  const payload = parts[1] ? decode(parts[1]) : "";
  return (
    <div className="space-y-4">
      <label className={label}>JWT token</label>
      <textarea data-testid="jwt-input" rows={3} className={input} value={tok} onChange={(e) => setTok(e.target.value)} />
      <div className="grid md:grid-cols-2 gap-4">
        <div><p className={label}>Header</p><pre className={pre} data-testid="jwt-header">{header}</pre></div>
        <div><p className={label}>Payload</p><pre className={pre} data-testid="jwt-payload">{payload}</pre></div>
      </div>
      <p className="text-xs text-slate-500">Note: This tool only decodes — it never verifies the signature.</p>
    </div>
  );
}

/* 21. Hash Generator */
function HashTool() {
  const [text, setText] = useState("hello, world");
  const [algo, setAlgo] = useState("SHA-256");
  const [out, setOut] = useState("");
  const run = async () => {
    const enc = new TextEncoder().encode(text);
    const buf = await crypto.subtle.digest(algo, enc);
    const hex = Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
    setOut(hex);
  };
  React.useEffect(() => { run(); /* eslint-disable-next-line */ }, [text, algo]);
  return (
    <div className="space-y-4">
      <label className={label}>Input</label>
      <textarea data-testid="hash-input" rows={4} className={inputNorm} value={text} onChange={(e) => setText(e.target.value)} />
      <div className="flex gap-2 flex-wrap">
        {["SHA-1", "SHA-256", "SHA-384", "SHA-512"].map((a) => (
          <button key={a} onClick={() => setAlgo(a)} data-testid={`hash-${a}`} className={`px-3 py-1.5 rounded-lg text-sm border ${algo === a ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200"}`}>{a}</button>
        ))}
      </div>
      <pre className={pre} data-testid="hash-output">{out}</pre>
      <button className={ghost} onClick={() => copy(out)}><Copy className="w-3.5 h-3.5" /> Copy</button>
    </div>
  );
}

/* 22. Emoji Remover */
function EmojiStripTool() {
  const [text, setText] = useState("Look at this 🎉 great launch 🚀 — the team is on fire 🔥!");
  const stripped = useMemo(() => text.replace(/[\p{Extended_Pictographic}\p{Emoji_Component}]/gu, "").replace(/\s+/g, " ").trim(), [text]);
  return (
    <div className="space-y-4">
      <label className={label}>Input</label>
      <textarea data-testid="emoji-input" rows={6} className={inputNorm} value={text} onChange={(e) => setText(e.target.value)} />
      <label className={label}>Clean text</label>
      <pre className={pre} data-testid="emoji-output">{stripped}</pre>
      <button className={ghost} onClick={() => copy(stripped)}><Copy className="w-3.5 h-3.5" /> Copy clean</button>
    </div>
  );
}

/* 23. Meta / OG Preview */
function MetaPreviewTool() {
  const [f, setF] = useState({ title: "Julius Mason — Digital Entrepreneur", desc: "I help businesses grow through technology, marketing, and education.", url: "juliusmason.com", img: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div><label className={label}>Title (~60 chars)</label><input data-testid="meta-title" className={inputNorm} value={f.title} onChange={set("title")} maxLength={70} /><p className="text-xs text-slate-500 mt-1">{f.title.length}/60</p></div>
        <div><label className={label}>Description (~155 chars)</label><textarea data-testid="meta-desc" rows={3} className={inputNorm} value={f.desc} onChange={set("desc")} maxLength={200} /><p className="text-xs text-slate-500 mt-1">{f.desc.length}/155</p></div>
        <div><label className={label}>URL / Site</label><input className={inputNorm} value={f.url} onChange={set("url")} /></div>
        <div><label className={label}>Image URL (optional)</label><input className={inputNorm} value={f.img} onChange={set("img")} placeholder="https://…" /></div>
      </div>
      <div className="space-y-5">
        <div>
          <p className={label}>Google Search</p>
          <div className="border border-slate-100 rounded-xl p-4 bg-white" data-testid="meta-preview-google">
            <p className="text-xs text-slate-500 mb-1 truncate">{f.url}</p>
            <p className="text-blue-800 text-lg leading-snug line-clamp-2">{f.title}</p>
            <p className="text-sm text-slate-600 line-clamp-2 mt-1">{f.desc}</p>
          </div>
        </div>
        <div>
          <p className={label}>Twitter / X card</p>
          <div className="border border-slate-100 rounded-xl overflow-hidden bg-white" data-testid="meta-preview-twitter">
            {f.img && <div className="aspect-[1.91/1] bg-slate-100"><img src={f.img} alt="og" className="w-full h-full object-cover" /></div>}
            <div className="p-3">
              <p className="text-xs text-slate-500">{f.url}</p>
              <p className="font-medium leading-snug line-clamp-2">{f.title}</p>
              <p className="text-xs text-slate-600 line-clamp-2 mt-1">{f.desc}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* 24. Percentage Calculator */
function PercentTool() {
  const [mode, setMode] = useState("of");
  const [a, setA] = useState(20);
  const [b, setB] = useState(80);
  const calc = useMemo(() => {
    if (mode === "of") return { result: ((a / 100) * b).toFixed(2), formula: `${a}% × ${b} = ${a}/100 × ${b}`, note: `${a}% of ${b} is the same as ${b} × 0.${String(a).padStart(2, "0").slice(-2)} — move the decimal twice.` };
    if (mode === "isPct") return b === 0 ? { result: "—", formula: "Y can't be 0", note: "" } : { result: ((a / b) * 100).toFixed(2) + "%", formula: `${a} ÷ ${b} × 100`, note: `${a} is ${((a / b) * 100).toFixed(1)}% of ${b} — so ${b} is ${((b / a) * 100).toFixed(0)}% of ${a}.` };
    if (mode === "change") return b === 0 ? { result: "—", formula: "Y can't be 0", note: "" } : { result: (((a - b) / b) * 100).toFixed(2) + "%", formula: `(${a} − ${b}) ÷ ${b} × 100`, note: ((a - b) / b) < 0 ? `A ${Math.abs((a - b) / b * 100).toFixed(1)}% drop needs a ${((b / a - 1) * 100).toFixed(1)}% gain to recover — losses and gains are not symmetric.` : `To undo this gain you'd only give back ${((1 - b / a) * 100).toFixed(1)}%.` };
    if (mode === "increase") return { result: (b * (1 + a / 100)).toFixed(2), formula: `${b} × (1 + ${a}/100)`, note: `The reverse — taking ${a}% OFF the result — returns ${(b * (1 + a / 100) * (1 - a / 100)).toFixed(2)}, not ${b}. Percentages compound.` };
    return { result: "", formula: "", note: "" };
  }, [mode, a, b]);
  const modes = [
    ["of", "X% of Y", "%", ""],
    ["isPct", "X is what % of Y", "", ""],
    ["change", "% change from Y to X", "", ""],
    ["increase", "Increase Y by X%", "%", ""],
  ];
  const refs = [["10%", "÷ 10", "Move the decimal one place left"], ["5%", "10% halved", "Half of the 10% figure"], ["20%", "÷ 5", "One fifth"], ["25%", "÷ 4", "A quarter"], ["50%", "÷ 2", "Half"]];
  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">{modes.map(([k, l]) => <button key={k} onClick={() => setMode(k)} data-testid={`pct-${k}`} className={`px-3 py-1.5 rounded-lg text-sm border ${mode === k ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200"}`}>{l}</button>)}</div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={label}>{mode === "of" || mode === "increase" ? "X (the %)" : "X (new/part value)"}</label><input data-testid="pct-a" type="number" className={inputNorm} value={a} onChange={(e) => setA(Number(e.target.value))} /></div>
        <div><label className={label}>{mode === "of" || mode === "increase" ? "Y (the amount)" : "Y (base value)"}</label><input data-testid="pct-b" type="number" className={inputNorm} value={b} onChange={(e) => setB(Number(e.target.value))} /></div>
      </div>
      <div className="border border-slate-100 rounded-xl p-6 bg-slate-50/60"><p className="text-[10px] uppercase font-mono tracking-widest text-slate-500 mb-1">Result</p><p className="font-display text-4xl" data-testid="pct-result">{calc.result}</p>
        {calc.formula && <p className="font-mono text-xs text-slate-500 mt-2">= {calc.formula}</p>}
        {calc.note && <p className="text-sm text-slate-600 border-l-2 border-indigo-300 pl-3 mt-3 leading-relaxed" data-testid="pct-note">{calc.note}</p>}
      </div>
      <div className="overflow-x-auto border border-slate-100 rounded-lg" data-testid="pct-cheatsheet">
        <table className="w-full text-xs">
          <thead><tr className="bg-slate-50 text-slate-500 uppercase tracking-wider"><th className="text-left px-3 py-2 font-semibold">Mental math</th><th className="text-left px-3 py-2 font-semibold">Shortcut</th><th className="text-left px-3 py-2 font-semibold">How</th></tr></thead>
          <tbody className="font-mono">{refs.map((r, i) => <tr key={i} className="border-t border-slate-100">{r.map((c, j) => <td key={j} className="px-3 py-1.5">{c}</td>)}</tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

/* 25. Age & Date Calculator */
function AgeCalcTool() {
  const [start, setStart] = useState("1995-06-15");
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const diff = useMemo(() => {
    try {
      const s = new Date(start), e = new Date(end);
      if (isNaN(s) || isNaN(e)) return null;
      let y = e.getFullYear() - s.getFullYear();
      let m = e.getMonth() - s.getMonth();
      let d = e.getDate() - s.getDate();
      if (d < 0) { m -= 1; d += new Date(e.getFullYear(), e.getMonth(), 0).getDate(); }
      if (m < 0) { y -= 1; m += 12; }
      const totalDays = Math.round((e - s) / 86400000);
      const weekday = s.toLocaleDateString("en-US", { weekday: "long" });
      const Z = [["Capricorn", 119], ["Aquarius", 218], ["Pisces", 320], ["Aries", 419], ["Taurus", 520], ["Gemini", 620], ["Cancer", 722], ["Leo", 822], ["Virgo", 922], ["Libra", 1022], ["Scorpio", 1121], ["Sagittarius", 1221], ["Capricorn", 1231]];
      const md = (s.getMonth() + 1) * 100 + s.getDate();
      const zodiac = Z.find(([, lim]) => md <= lim)?.[0] || "Capricorn";
      return { y, m, d, totalDays, weekday, zodiac };
    } catch { return null; }
  }, [start, end]);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <label className="block"><span className={label}>From</span><input data-testid="age-from" type="date" className={inputNorm} value={start} onChange={(e) => setStart(e.target.value)} /></label>
        <label className="block"><span className={label}>To</span><input data-testid="age-to" type="date" className={inputNorm} value={end} onChange={(e) => setEnd(e.target.value)} /></label>
      </div>
      {diff && (
        <>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[["Years", diff.y, "age-years"], ["Months", diff.m, "age-months"], ["Days", diff.d, "age-days"], ["Total days", diff.totalDays.toLocaleString(), "age-total"]].map(([k, v, tid]) => (
            <div key={k} className="border border-slate-100 rounded-lg p-4 bg-slate-50/60"><p className="text-[10px] uppercase font-mono tracking-widest text-slate-500 mb-1">{k}</p><p className="font-display text-2xl" data-testid={tid}>{v}</p></div>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="age-extra">
          <div className="border border-slate-100 rounded-lg p-4 bg-slate-50/60"><p className="text-[10px] uppercase font-mono tracking-widest text-slate-500 mb-1">Day of week</p><p className="font-display text-lg">{diff.weekday}</p></div>
          <div className="border border-slate-100 rounded-lg p-4 bg-slate-50/60"><p className="text-[10px] uppercase font-mono tracking-widest text-slate-500 mb-1">Total hours</p><p className="font-display text-lg">{(diff.totalDays * 24).toLocaleString()}</p></div>
          <div className="border border-slate-100 rounded-lg p-4 bg-slate-50/60"><p className="text-[10px] uppercase font-mono tracking-widest text-slate-500 mb-1">Heartbeats*</p><p className="font-display text-lg">{Math.round(diff.totalDays * 24 * 60 * 70 / 1e6).toLocaleString()} M</p></div>
          <div className="border border-slate-100 rounded-lg p-4 bg-slate-50/60"><p className="text-[10px] uppercase font-mono tracking-widest text-slate-500 mb-1">Star sign</p><p className="font-display text-lg">{diff.zodiac}</p></div>
        </div>
        <p className="text-xs text-slate-400">*At an average 70 beats per minute. Day of week and star sign use the start date.</p>
        </>
      )}
    </div>
  );
}

export const MORE_TOOLS = [
  { slug: "json-formatter", name: "JSON Formatter & Validator", desc: "Pretty-print, minify and validate JSON in your browser.", icon: Braces, category: "Developer", pro: false, span: "md:col-span-2", Component: JsonTool },
  { slug: "base64", name: "Base64 Encoder / Decoder", desc: "Encode text to Base64 and back, safe for URLs & APIs.", icon: Binary, category: "Developer", pro: false, span: "md:col-span-1", Component: Base64Tool },
  { slug: "url-encoder", name: "URL Encoder / Decoder", desc: "Percent-encode strings for safe URLs and query params.", icon: LinkIcon, category: "Developer", pro: false, span: "md:col-span-1", Component: UrlEncoderTool },
  { slug: "markdown", name: "Markdown Live Preview", desc: "Type Markdown, get instant HTML preview & copyable output.", icon: FileCode, category: "Writing", pro: false, span: "md:col-span-2", Component: MarkdownTool },
  { slug: "regex-tester", name: "Regex Tester", desc: "Test JavaScript regex against any string with live highlights.", icon: Regex, category: "Developer", pro: false, span: "md:col-span-2", Component: RegexTool },
  { slug: "uuid", name: "UUID Generator", desc: "Generate cryptographically-random UUIDs by the batch.", icon: Fingerprint, category: "Developer", pro: false, span: "md:col-span-1", Component: UuidTool },
  { slug: "password-strength", name: "Password Strength Checker", desc: "See how tough your password really is — locally, never sent.", icon: ShieldCheck, category: "Security", pro: false, span: "md:col-span-1", Component: StrengthTool },
  { slug: "color-palette", name: "Color Palette Generator", desc: "Pick a color and get tints, shades, and complements.", icon: Palette, category: "Design", pro: false, span: "md:col-span-2", Component: ColorTool },
  { slug: "gradient", name: "CSS Gradient Generator", desc: "Design gradients visually and copy production CSS.", icon: Blend, category: "Design", pro: false, span: "md:col-span-1", Component: GradientTool },
  { slug: "youtube-thumb", name: "YouTube Thumbnail Grabber", desc: "Download any YouTube video thumbnail in every size.", icon: Youtube, category: "Marketing", pro: false, span: "md:col-span-1", Component: YoutubeTool },
  { slug: "slug", name: "SEO Slug Generator", desc: "Turn any title into a clean, SEO-friendly URL slug.", icon: Slash, category: "SEO", pro: false, span: "md:col-span-1", Component: SlugTool },
  { slug: "text-diff", name: "Text Diff Checker", desc: "Compare two blocks of text word-by-word.", icon: Diff, category: "Writing", pro: false, span: "md:col-span-2", Component: DiffTool },
  { slug: "unit-converter", name: "Unit Converter", desc: "Length, weight & temperature — instantly, offline.", icon: Ruler, category: "Utility", pro: false, span: "md:col-span-1", Component: UnitTool },
  { slug: "timestamp", name: "Timestamp Converter", desc: "Unix ↔ human-readable date, both directions.", icon: Clock, category: "Developer", pro: false, span: "md:col-span-1", Component: TimestampTool },
  { slug: "image-base64", name: "Image → Base64 Converter", desc: "Convert any image into a Data URL for CSS or emails.", icon: ImageDown, category: "Media", pro: false, span: "md:col-span-1", Component: ImageBase64Tool },
  { slug: "utm-builder", name: "UTM Link Builder", desc: "Build clean UTM-tagged URLs for your campaigns.", icon: Tag, category: "Marketing", pro: false, span: "md:col-span-2", Component: UtmBuilderTool },
  { slug: "word-counter", name: "Word & Character Counter", desc: "Live word, character, sentence & reading-time count.", icon: AlignLeft, category: "Writing", pro: false, span: "md:col-span-1", Component: WordCounterTool },
  { slug: "contrast-checker", name: "Color Contrast Checker", desc: "WCAG contrast score for any foreground/background pair.", icon: Contrast, category: "Design", pro: false, span: "md:col-span-2", Component: ContrastTool },
  { slug: "password-generator", name: "Password Generator", desc: "Cryptographically-secure passwords with your rules.", icon: KeyRound, category: "Security", pro: false, span: "md:col-span-1", Component: PasswordGenTool },
  { slug: "jwt-decoder", name: "JWT Decoder", desc: "Decode header + payload from any JSON Web Token.", icon: FileJson, category: "Developer", pro: false, span: "md:col-span-2", Component: JwtDecoderTool },
  { slug: "hash-generator", name: "Hash Generator", desc: "SHA-256, SHA-512 & SHA-1 hashes of any string.", icon: Hash, category: "Security", pro: false, span: "md:col-span-1", Component: HashTool },
  { slug: "emoji-remover", name: "Emoji Remover", desc: "Strip emoji and unicode symbols from any text.", icon: Smile, category: "Writing", pro: false, span: "md:col-span-1", Component: EmojiStripTool },
  { slug: "meta-preview", name: "Meta / OG Preview", desc: "See exactly how your page will look on Google, X, LinkedIn.", icon: Globe2, category: "SEO", pro: false, span: "md:col-span-2", Component: MetaPreviewTool },
  { slug: "percent-calc", name: "Percentage Calculator", desc: "Discounts, tax, tips, growth-rate — all in one place.", icon: Percent, category: "Utility", pro: false, span: "md:col-span-1", Component: PercentTool },
  { slug: "age-calculator", name: "Age & Date Calculator", desc: "Years-months-days between any two dates.", icon: Calendar, category: "Utility", pro: false, span: "md:col-span-1", Component: AgeCalcTool },
];
